/**
 * @file page-form.ts
 * @description Поля страницы сайта: название, адрес, поиск (phase19.16
 * Р1-6а; uiux-builder-shell.md §2.2 «Настройки страницы», «Новая страница»).
 *
 * Лимиты — как у сервера (`CreateSitePageDto`/`UpdateSitePageDto`): адрес
 * 1–80 знаков `a-z0-9-`; мягкие подсказки поиска — 60 и 160 знаков (поиск
 * обрежет длиннее). Адрес из названия — латиницей по транслитерации.
 * Тексты — словами владельца («адрес страницы», не slug).
 * v0.19.65 (SB146): «Хлебные крошки» страницы — `breadcrumbs`.
 */

import type { SitePageBreadcrumbs } from "@chaiberi/shared";

export const PAGE_TITLE_MAX = 80;
export const PAGE_SLUG_MAX = 80;
export const PAGE_SEO_TITLE_SOFT = 60;
export const PAGE_SEO_TITLE_MAX = 200;
export const PAGE_SEO_DESCRIPTION_SOFT = 160;
export const PAGE_SEO_DESCRIPTION_MAX = 400;

export interface PageForm {
  title: string;
  slug: string;
  seoTitle: string;
  seoDescription: string;
  noIndex: boolean;
  /** v0.19.65 (SB146): «как на сайте / показывать / скрыть». */
  breadcrumbs: SitePageBreadcrumbs;
}

export type PageFormErrors = Partial<Record<keyof PageForm, string>>;

const TRANSLIT: Readonly<Record<string, string>> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m",
  н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts", ч: "ch", ш: "sh", щ: "sch",
  ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};

/** Адрес из названия: «О мастерской» → `o-masterskoy`. */
export function slugFromTitle(title: string): string {
  return [...title.toLowerCase()]
    .map((ch) => TRANSLIT[ch] ?? ch)
    .join("")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, PAGE_SLUG_MAX)
    .replace(/-+$/, "");
}

/** Ввод адреса вручную: только разрешённые знаки. */
export function cleanSlugInput(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, PAGE_SLUG_MAX);
}

/** Ошибки полей; пусто — можно сохранять. `slugLocked` — адрес главной не меняется. */
export function pageFormErrors(form: Omit<PageForm, "breadcrumbs">, slugLocked = false): PageFormErrors {
  const errors: PageFormErrors = {};
  const title = form.title.trim();
  if (!title) errors.title = "Нужно название — его видно в меню и во вкладке браузера.";
  else if (title.length > PAGE_TITLE_MAX) errors.title = `Не длиннее ${PAGE_TITLE_MAX} знаков.`;
  if (!slugLocked) {
    if (!form.slug) errors.slug = "Нужен адрес страницы, например about.";
    else if (!/^[a-z0-9-]+$/.test(form.slug)) errors.slug = "Только латиница, цифры и дефис.";
  }
  if (form.seoTitle.length > PAGE_SEO_TITLE_MAX) errors.seoTitle = `Не длиннее ${PAGE_SEO_TITLE_MAX} знаков.`;
  if (form.seoDescription.length > PAGE_SEO_DESCRIPTION_MAX) errors.seoDescription = `Не длиннее ${PAGE_SEO_DESCRIPTION_MAX} знаков.`;
  return errors;
}

/** Ошибки сервера → поле формы или общий текст (адрес занят, зарезервирован). */
export const PAGE_ERROR_FIELDS: Readonly<Record<string, { field: keyof PageForm; text: string }>> = {
  site_page_slug_taken: { field: "slug", text: "Такой адрес уже есть у другой страницы." },
  site_page_slug_reserved: { field: "slug", text: "Этот адрес занят разделом магазина — выберите другой." },
  site_page_system_slug_immutable: { field: "slug", text: "Адрес главной страницы не меняется." },
};
